import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import { grossMarginFor } from "../wal-ftp";

/**
 * `ALL_RATINGS` is a real stored value, not a UI placeholder, so it belongs in
 * the union. 'Other' is a real rating in the data alongside A/B1/B2/B3.
 */
export type PolicyRating = "A" | "B1" | "B2" | "B3" | "Other" | "All Ratings";
/** The pickable specific ratings; ALL_RATINGS is offered separately. */
export const POLICY_RATINGS: PolicyRating[] = ["A", "B1", "B2", "B3", "Other"];

/**
 * One row of the (office, industry, rating) policy grid.
 *
 * The grid is SPARSE: it always holds the All Offices × All Industries × All
 * Ratings row, and gains a narrower row only when a proposal for that exact
 * scope is approved. So a deal in an undecided scope resolves to the broadest
 * row covering it — see `resolvePolicy`, which mirrors the backend's rule.
 *
 * rbpEnabled is a two-rung LADDER, not one value per office: it is set on the
 * office's All Industries/All Ratings row or on an industry's All Ratings row,
 * and `null` means "not decided at this rung — inherit from the one above".
 * Read it with `rbpEnabledFor`, never directly.
 */
export interface TargetPolicy {
  /** "office|industry|rating" — the grid has no surrogate key. */
  id: string;
  office: string;
  industry: string;
  rating: PolicyRating;
  opex: number;
  targetNetMargin: number;
  /** Percent of gross margin paid out as internal commission (18 = 18%). */
  internalCommissionRate: number;
  /**
   * RBP switch for THIS ROW's rung only — null/undefined means undecided here,
   * so it inherits. Do not read this field directly to answer "is this priced";
   * that is `rbpEnabledFor(policies, office, industry)`.
   */
  rbpEnabled?: boolean | null;
  updatedAt: string;
}

/**
 * One rendered row of the Active Policy table, office-led: either an office's
 * own default row or one of its industry overrides. Built client-side by
 * `buildPolicyRows` from the full `/target-policies` fetch for a single
 * office/industry tab (a small, bounded set), or returned already-built by
 * `GET /target-policies/page` for the "All Offices" aggregate view, where the
 * row count isn't bounded and a real page is fetched from the server instead.
 * Both paths produce this exact shape so the table component doesn't care
 * which one supplied a given row.
 */
export interface PolicyRow {
  key: string;
  office: string;
  industry?: string;
  opex: number | null;
  targetNetMargin: number | null;
  internalCommissionRate: number | null;
  /** Resolved through the ladder (office rung, then industry rung), NOT copied
   *  off the office row -- an industry can now differ from its office. */
  rbpEnabled: boolean;
  /** True when the office rung itself is off, so every industry under it is off
   *  regardless of its own setting. Drives the disabled state of the row's
   *  switch: you cannot enable an industry inside a switched-off office. */
  officeDisabled: boolean;
  updatedAt: string | null;
  /** False when this row inherits its values from a broader grid row rather
   *  than having one of its own — shown as a hint, since the numbers are real
   *  either way (they are what pricing actually applies). */
  ownRow: boolean;
}

/** The REVERSE of PolicyRow: industry-led rather than office-led. See PolicyRow's
 *  own doc comment -- same two sources (buildPolicyRowsByIndustry for one
 *  industry/office tab, GET /target-policies/page?ladder=industry for "All
 *  Industries"), same shared shape. */
export interface PolicyRowByIndustry {
  key: string;
  industry: string;
  office?: string;
  opex: number | null;
  targetNetMargin: number | null;
  internalCommissionRate: number | null;
  /** Resolved through the REVERSE ladder (industry rung, then office rung). */
  rbpEnabled: boolean;
  /** True when the industry rung itself is off, so every office under it is off
   *  regardless of its own setting -- mirrors PolicyRow.officeDisabled, roles swapped. */
  industryDisabled: boolean;
  updatedAt: string | null;
  ownRow: boolean;
}

/** Shared envelope for every server-paginated list on this page (Active
 *  Policy's aggregate view, Permanent Log) -- one page of `items`, the total
 *  matching row count, and whether another page remains. */
export interface PagedResult<T> {
  items: T[];
  total: number;
  hasMore: boolean;
}

export type TargetProposalKind = "targets" | "rbp-toggle";

export interface TargetProposal {
  id: string;
  kind: TargetProposalKind;
  office: string;
  industry: string;
  rating: PolicyRating;
  currentOpex: number | null;
  proposedOpex: number;
  currentTargetNetMargin: number | null;
  proposedTargetNetMargin: number;
  currentInternalCommissionRate: number | null;
  proposedInternalCommissionRate: number;
  /** rbp-toggle proposals only. */
  currentRbpEnabled?: boolean;
  proposedRbpEnabled?: boolean;
  reason: string;
  proposedBy: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: string;
  reviewNotes?: string;
  reviewedAt?: string;
}

export interface SyncStatus {
  lastSentToUE: string | null;
  lastSentToSalesforce: string | null;
}

/** Append-only governance log; nothing is ever deleted or edited. */
export interface TargetLogEntry {
  id: string;
  kind: TargetProposalKind;
  office: string;
  industry: string;
  rating: PolicyRating;
  oldOpex: number | null;
  newOpex: number;
  oldTargetNetMargin: number | null;
  newTargetNetMargin: number;
  oldInternalCommissionRate: number | null;
  newInternalCommissionRate: number;
  oldRbpEnabled?: boolean;
  newRbpEnabled?: boolean;
  effectiveFrom: string;
  proposedBy: string;
  reason: string;
  reviewedBy?: string;
  reviewNotes?: string;
  /** SUPERSEDED = a later approval of the same kind covered this scope, so the
   *  grid no longer holds these values. */
  status: "APPROVED" | "REJECTED" | "SUPERSEDED";
  /** The proposal id. There is no policy version any more (the grid is updated
   *  in place and keeps none); rendered as an opaque reference string. */
  policyVersionId: string;
  /** How many grid rows this approval's fan-out touched. */
  rowsUpdated?: number | null;
  /**
   * Deals priced under this policy version. Becomes a join on DealPricingRecord
   * once the Live Pricing API exists; a plausible mock count until then.
   */
  dealsPriced: number;
}

/**
 * "Office" here is a real office (`office_name`), not a fixed taxonomy --
 * the actual list is fetched live via `useOffices()` (see ./reference).
 * "Industry" is fetched the same way via `useIndustries()`. Neither is a
 * fixed nested map anymore: there's no confirmed per-office industry list.
 */
/**
 * These are REAL stored values in `target_policies` / `target_proposals_history`,
 * not client-side placeholders that get stripped before the request. They must
 * match ALL_OFFICES / ALL_INDUSTRIES / ALL_RATINGS in the backend's
 * app/repository/target_policy.py and the CHECK constraints in
 * sql/003_target_tables_redesign.sql byte-for-byte -- a mismatch does not
 * error, it silently stops resolving.
 */
export const ALL_OFFICES = "All Offices";
export const ALL_INDUSTRIES = "All Industries";
export const ALL_RATINGS = "All Ratings";

/**
 * FTP, expected loss and external commission are read-only context on this page —
 * they come from the Treasury curve, Calibration, and the vendor/broker contracts.
 */
export interface ReadOnlyContext {
  /** Assumed weighted average life for the segment, in years. */
  walYears: number;
  ftp: number;
  expectedLoss: number;
  externalCommission: number;
}

/** One row of `GET /policy-context` — real WAL/FTP/expected-loss/external-
 * commission figures from `wal_grouping_sic_office`, keyed by office name
 * (matching every other identity on this page) and industry. `industry: null`
 * is the office-level rollup, used for office-only rows and as the fallback
 * when a specific industry has no row of its own. */
export interface PolicyContextRow {
  office: string;
  industry: string | null;
  walYears: number;
  ftp: number;
  expectedLoss: number;
  externalCommission: number;
}

export function usePolicyContext() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["manage-targets", "policy-context", ticket ?? "all"],
    queryFn: () => apiGet<PolicyContextRow[]>("/policy-context", { ticket }),
    staleTime: 60_000,
  });
}

const ZERO_CONTEXT: ReadOnlyContext = { walYears: 0, ftp: 0, expectedLoss: 0, externalCommission: 0 };

function averageContext(rows: PolicyContextRow[]): ReadOnlyContext {
  if (rows.length === 0) return ZERO_CONTEXT;
  const sum = rows.reduce(
    (acc, row) => ({
      walYears: acc.walYears + row.walYears,
      ftp: acc.ftp + row.ftp,
      expectedLoss: acc.expectedLoss + row.expectedLoss,
      externalCommission: acc.externalCommission + row.externalCommission,
    }),
    { walYears: 0, ftp: 0, expectedLoss: 0, externalCommission: 0 },
  );
  return {
    walYears: sum.walYears / rows.length,
    ftp: sum.ftp / rows.length,
    expectedLoss: sum.expectedLoss / rows.length,
    externalCommission: sum.externalCommission / rows.length,
  };
}

function toReadOnlyContext(row: PolicyContextRow): ReadOnlyContext {
  return {
    walYears: row.walYears,
    ftp: row.ftp,
    expectedLoss: row.expectedLoss,
    externalCommission: row.externalCommission,
  };
}

/**
 * A specificity cascade over `wal_grouping_sic_office`'s real coverage gaps --
 * same "sparse grid, fall back to the broadest thing that covers you" shape as
 * `target_policy.py`'s `resolve_policy`, applied to this read-only context
 * instead of the editable policy. The grid here has no All-Offices row of its
 * own (context is always real-office-keyed), so "All Offices" is a computed
 * average over whichever real offices have a matching row, not a lookup.
 *
 * Cascade, most to least specific:
 *   1. This office + this industry (its own row).
 *   2. This office + All Industries (its own rollup row) -- same office,
 *      broader industry. Preferred over step 3 because it's still this
 *      office's own real data, not an average over unrelated offices.
 *   3. All Offices + this industry (averaged across every office that
 *      reports it) -- same industry, broader office.
 *   4. All Offices + All Industries (averaged across every office's own
 *      rollup row) -- the broadest real data there is.
 *   5. Zero, only if the grid has no data at all for #4 either.
 *
 * `industry: undefined` means no industry filter (callers resolve their own
 * "Office default"/`ALL_INDUSTRIES` sentinels to undefined before calling
 * this) -- skips straight past the industry-specific steps (1 with an
 * industry, 3) to whichever office-level step applies.
 */
export function readOnlyContextFor(
  context: PolicyContextRow[],
  office: string,
  industry?: string,
): ReadOnlyContext {
  if (office !== ALL_OFFICES) {
    const exact = industry
      ? context.find((row) => row.office === office && row.industry === industry)
      : context.find((row) => row.office === office && row.industry == null);
    if (exact) return toReadOnlyContext(exact);

    if (industry) {
      const officeTotal = context.find((row) => row.office === office && row.industry == null);
      if (officeTotal) return toReadOnlyContext(officeTotal);
    }
  }

  if (industry) {
    const industryRows = context.filter((row) => row.industry === industry);
    if (industryRows.length) return averageContext(industryRows);
  }

  const portfolioRows = context.filter((row) => row.industry == null);
  return portfolioRows.length ? averageContext(portfolioRows) : ZERO_CONTEXT;
}

export { grossMarginFor };

/** requiredYield = ftp + expectedLoss + opex + externalCommission + grossMargin */
export function requiredYield(
  context: ReadOnlyContext,
  opex: number,
  targetNetMargin: number,
  internalCommissionRate: number,
): number {
  const grossMargin = grossMarginFor(targetNetMargin, internalCommissionRate);
  return Number(
    (
      context.ftp +
      context.expectedLoss +
      opex +
      context.externalCommission +
      grossMargin
    ).toFixed(2),
  );
}

/** How many of the three dimensions are pinned to a real value rather than a
 *  sentinel. 0 is the All/All/All row; 3 is a fully-specified combination. */
function specificity(policy: TargetPolicy): number {
  return (
    (policy.office !== ALL_OFFICES ? 1 : 0) +
    (policy.industry !== ALL_INDUSTRIES ? 1 : 0) +
    (policy.rating !== ALL_RATINGS ? 1 : 0)
  );
}

/**
 * Which grid row applies to one (office, industry, rating) — most specific
 * wins, ties between incomparable scopes broken by the most recently updated.
 * This mirrors `resolve_policy` in the backend's target_policy.py exactly; the
 * two must not drift, since that one drives the actual pricing formulas.
 *
 * `undefined` for a dimension means the caller can't express it at this grain,
 * and only matches rows that are themselves "all" on it — the conservative
 * reading: don't apply one industry's policy to a figure spanning all of them.
 *
 * Returns null only when the grid is empty, which a correctly-migrated
 * database never is — the All/All/All row matches everything.
 */
export function resolvePolicy(
  policies: TargetPolicy[],
  office: string,
  industry?: string,
  rating?: PolicyRating,
): TargetPolicy | null {
  let best: TargetPolicy | null = null;
  for (const policy of policies) {
    const matches =
      (policy.office === ALL_OFFICES || policy.office === office) &&
      (policy.industry === ALL_INDUSTRIES || policy.industry === industry) &&
      (policy.rating === ALL_RATINGS || policy.rating === rating);
    if (!matches) continue;
    if (
      best == null ||
      specificity(policy) > specificity(best) ||
      (specificity(policy) === specificity(best) && policy.updatedAt > best.updatedAt)
    ) {
      best = policy;
    }
  }
  return best;
}

/** True when this exact scope has a row of its own, rather than inheriting one
 *  from a broader row. Used to decide whether a scope is worth listing. */
export function hasOwnPolicy(
  policies: TargetPolicy[],
  office: string,
  industry: string = ALL_INDUSTRIES,
): boolean {
  return policies.some((p) => p.office === office && p.industry === industry);
}

/**
 * Cascading lookup for the internal commission rate. Kept as its own export
 * because callers only ever want this one field; `resolvePolicy` is the rule.
 */
export function internalCommissionRateFor(
  policies: TargetPolicy[],
  office: string,
  industry?: string,
  rating?: PolicyRating,
): number | null {
  const matched = resolvePolicy(policies, office, industry, rating);
  return matched ? matched.internalCommissionRate : null;
}

/**
 * Is risk-based pricing on for this (office, industry)? The LADDER.
 *
 * Two rungs, and they mean different things:
 *   - OFFICE off   — nothing in the office is risk-based priced at all.
 *   - INDUSTRY off — that industry is still priced, but off the office's
 *                    All Industries targets rather than its own.
 *
 * So office-off ALWAYS wins: an industry left enabled underneath a disabled
 * office must not resurrect pricing there.
 *
 * The switch is stored at the rung it was decided on and inherited downward;
 * `rbpEnabled == null` means "not decided here, ask the rung above", and an
 * undecided office is on. Mirrors the backend's
 * `target_policy.resolve_rbp_enabled` and SQL `rbp_enabled_for()` — all three
 * must agree.
 *
 * Pass `industry` undefined to ask about the office as a whole.
 */
export function rbpEnabledFor(
  policies: TargetPolicy[],
  office: string,
  industry?: string,
): boolean {
  // Exact rung lookups, not resolvePolicy's specificity cascade: absence is
  // meaningful here (it means "inherit"), not something to fall back through.
  const rung = (ind: string) =>
    policies.find(
      (p) => p.office === office && p.industry === ind && p.rating === ALL_RATINGS,
    );

  const officeRung = rung(ALL_INDUSTRIES);
  if (officeRung?.rbpEnabled === false) return false;

  if (industry != null && industry !== ALL_INDUSTRIES) {
    const industryRung = rung(industry);
    if (industryRung?.rbpEnabled != null) return industryRung.rbpEnabled;
  }

  return officeRung?.rbpEnabled ?? true;
}

/**
 * The REVERSE ladder: industry is dominant, office is subordinate.
 *
 * Mirrors `rbpEnabledFor` exactly, with the two rungs' roles swapped:
 *   - INDUSTRY off — nothing in that industry is risk-based priced, in any office.
 *   - OFFICE off (within the industry) — that office is still priced, but off the
 *     industry's All Offices targets rather than its own.
 *
 * Industry-off always wins, same reasoning as office-off in the original ladder. Reuses
 * the same exact-rung lookup pattern `rbpEnabledFor` uses — absence is meaningful
 * ("inherit"), not something to fall back through via `resolvePolicy`'s specificity
 * cascade.
 *
 * The office rung here reads the SAME (office, industry, All Ratings) row `rbpEnabledFor`
 * reads as its own subordinate rung — shared storage between the two ladders, correct as
 * long as only one is treated as the one actually in force for a given (office, industry)
 * pair. See the backend's target_policy.py:resolve_rbp_enabled_by_industry for the fuller
 * reasoning; the two must agree, along with SQL's rbp_enabled_for_by_industry() (sql/006_).
 *
 * Pass `office` undefined to ask about the industry as a whole.
 */
export function rbpEnabledForByIndustry(
  policies: TargetPolicy[],
  industry: string,
  office?: string,
): boolean {
  const rung = (off: string) =>
    policies.find(
      (p) => p.office === off && p.industry === industry && p.rating === ALL_RATINGS,
    );

  const industryRung = rung(ALL_OFFICES);
  if (industryRung?.rbpEnabled === false) return false;

  if (office != null && office !== ALL_OFFICES) {
    const officeRung = rung(office);
    if (officeRung?.rbpEnabled != null) return officeRung.rbpEnabled;
  }

  return industryRung?.rbpEnabled ?? true;
}

/**
 * Which offices are priced by RBP at all — sourced entirely from policy, no
 * fixed default list. Office-level only: read off each office's broadest row,
 * which is the office rung of the ladder above. An office with no row of its
 * own is absent from the map, so callers should treat a missing key as enabled
 * (`map[office] !== false`).
 *
 * Industry-level switches deliberately do NOT affect this — State of Play's
 * "Offices Actively Priced" counts offices, and an office with one industry on
 * office targets is still an actively priced office. Use `rbpEnabledFor` for
 * anything at industry grain.
 */
export function rbpEnabledMap(policies: TargetPolicy[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const policy of policies) {
    if (
      policy.industry === ALL_INDUSTRIES &&
      policy.rating === ALL_RATINGS &&
      policy.rbpEnabled != null
    ) {
      map[policy.office] = policy.rbpEnabled;
    }
  }
  return map;
}

export const manageTargetKeys = {
  policies: () => ["manage-targets", "policies"] as const,
  proposals: () => ["manage-targets", "proposals"] as const,
  // Prefix shared by every scoped log page below -- react-query's
  // invalidateQueries matches on prefix, so invalidating this root (see
  // useDecideTargetProposal) clears every office/industry-scoped page at
  // once, not just whichever scope happens to be on screen.
  logPageRoot: () => ["manage-targets", "log-page"] as const,
  logPage: (office?: string, industry?: string) =>
    ["manage-targets", "log-page", office ?? "all", industry ?? "all"] as const,
  activePolicyPage: (ladder: "office" | "industry", ticket?: TicketSize) =>
    ["manage-targets", "active-policy-page", ladder, ticket ?? "all"] as const,
  sync: () => ["manage-targets", "sync"] as const,
};

/** Page size for every server-paginated list on this page -- Active Policy's
 *  aggregate view and Permanent Log. Exported so ManageTargets.tsx's Prev/Next
 *  math (offset +/- PAGE_SIZE) can't drift from what these hooks actually request. */
export const PAGE_SIZE = 10;
const EMPTY_ACTIVE_POLICY_PAGE_ROWS: (PolicyRow | PolicyRowByIndustry)[] = [];
const EMPTY_LOG_PAGE_ENTRIES: TargetLogEntry[] = [];

function nextOffset<T>(pages: PagedResult<T>[]): number {
  return pages.reduce((n, page) => n + page.items.length, 0);
}

export function useTargetPolicies() {
  return useQuery({
    queryKey: manageTargetKeys.policies(),
    queryFn: () => apiGet<TargetPolicy[]>("/target-policies"),
    staleTime: 60_000,
  });
}

/**
 * Server-paginated Active Policy rows for the "All Offices"/"All Industries"
 * aggregate view -- PAGE_SIZE offices' (or industries') worth of already-
 * resolved rows per call, appended on `fetchNextPage()`. A specific office or
 * industry tab does NOT use this -- that view is already small and bounded
 * (one scope's own rows), and keeps building from the plain, unpaginated
 * `useTargetPolicies()` fetch via `buildPolicyRows`/`buildPolicyRowsByIndustry`.
 *
 * `ladder` selects which dimension is paginated (see GET /target-policies/page);
 * switching ladder starts a fresh query, not a refetch of the same pages.
 * `enabled` (default true) lets a caller that only needs this hook's data in
 * the "All Offices"/"All Industries" case skip firing it while a specific
 * office/industry tab is showing instead.
 */
export function useActivePolicyPage(ladder: "office" | "industry", enabled = true) {
  const { ticket } = useTicketSize();
  const query = useInfiniteQuery({
    queryKey: manageTargetKeys.activePolicyPage(ladder, ticket),
    queryFn: ({ pageParam }) =>
      apiGet<PagedResult<PolicyRow | PolicyRowByIndustry>>("/target-policies/page", {
        ladder,
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
        ticket,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? nextOffset(allPages) : undefined),
    staleTime: 60_000,
    enabled,
  });

  return {
    ...query,
    rows: query.data?.pages.flatMap((page) => page.items) ?? EMPTY_ACTIVE_POLICY_PAGE_ROWS,
    total: query.data?.pages[0]?.total ?? 0,
  };
}

export function useTargetProposals() {
  return useQuery({
    queryKey: manageTargetKeys.proposals(),
    queryFn: () => apiGet<TargetProposal[]>("/target-proposals"),
    staleTime: 60_000,
  });
}

/**
 * Permanent Log, paginated server-side, one PAGE_SIZE-row page per call --
 * the log is append-only and never deleted (see the backend's own
 * manage_targets.py docstring), so it has no natural ceiling the way the
 * policy grid does. Prev/Next over `offset` (same shape as
 * portfolio-risk-concentrations' `useFairValueSpreadProposals`/`FairValueLog`)
 * rather than an accumulating "load more" -- each `offset` is its own cached
 * page, so paging back to one already visited is instant.
 *
 * `office`/`industry` scope the query the SAME way ManageTargets.tsx's ladder
 * selection always has -- pass the currently-selected office (ladder="office")
 * or industry (ladder="industry"), or leave both undefined for "All
 * Offices"/"All Industries". Scoping happens server-side now, so a page is 10
 * rows of whatever's currently scoped, not 10 raw rows that shrink once
 * filtered client-side.
 */
export function useTargetLogPage(offset: number, office?: string, industry?: string) {
  const query = useQuery({
    queryKey: [...manageTargetKeys.logPage(office, industry), offset],
    queryFn: () =>
      apiGet<PagedResult<TargetLogEntry>>("/target-log", {
        ...(office ? { office } : {}),
        ...(industry ? { industry } : {}),
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }),
    staleTime: 60_000,
  });

  return {
    ...query,
    entries: query.data?.items ?? EMPTY_LOG_PAGE_ENTRIES,
    total: query.data?.total ?? 0,
  };
}

export function useSyncStatus() {
  return useQuery({
    queryKey: manageTargetKeys.sync(),
    queryFn: () => apiGet<SyncStatus>("/target-sync-status"),
    staleTime: 60_000,
  });
}

export interface TargetProposalDraft {
  kind: TargetProposalKind;
  office: string;
  /** Sentinels, never omitted — "all" is a real value the server stores. */
  industry: string;
  rating: PolicyRating;
  currentOpex: number | null;
  proposedOpex: number;
  currentTargetNetMargin: number | null;
  proposedTargetNetMargin: number;
  currentInternalCommissionRate: number | null;
  proposedInternalCommissionRate: number;
  currentRbpEnabled?: boolean;
  proposedRbpEnabled?: boolean;
  reason: string;
}

export function useProposeTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: TargetProposalDraft) => apiPost<TargetProposal>("/target-proposals", draft),
    onSuccess: () => {
      // The proposal is now real (persisted in target_proposals) -- refetch
      // rather than guess its server-assigned id/proposedBy/proposedAt.
      queryClient.invalidateQueries({ queryKey: manageTargetKeys.proposals() });
    },
  });
}

export function useDecideTargetProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; status: "approved" | "rejected"; notes: string }) => {
      await apiPost(`/target-proposals/${input.id}/decide`, {
        status: input.status,
        notes: input.notes,
      });
      return input;
    },
    onSuccess: () => {
      // Approving/rejecting now really moves the proposal out of the queue,
      // (re)writes target_policies, and extends the permanent log server-side
      // -- refetch all three instead of hand-patching a guessed shape
      // (matches the invalidate-and-refetch pattern portfolio-risk-
      // concentrations uses after its own propose/review mutations).
      queryClient.invalidateQueries({ queryKey: manageTargetKeys.proposals() });
      // Prefix matches, so this clears every office/industry-scoped log page
      // and both ladders' active-policy pages, not just whichever is on screen.
      queryClient.invalidateQueries({ queryKey: manageTargetKeys.logPageRoot() });
      queryClient.invalidateQueries({ queryKey: manageTargetKeys.policies() });
      queryClient.invalidateQueries({ queryKey: ["manage-targets", "active-policy-page"] });
    },
  });
}
